import React, { useState } from "react";
import { useQuote } from "../context/QuoteContext";
import { StorMiLogo } from "./StorMiLogo";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "../api";

const BG = "https://images.unsplash.com/photo-1684695749267-233af13276d0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBzdG9yYWdlJTIwd2FyZWhvdXNlJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzg0NTMxOTgzfDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useQuote();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showReset, setShowReset] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetToken] = useState(() => new URLSearchParams(window.location.search).get("reset_token") || "");
  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmDone, setConfirmDone] = useState(false);

  const doConfirm = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) { toast.error("La password deve avere almeno 8 caratteri."); return; }
    if (newPw !== newPw2) { toast.error("Le password non coincidono."); return; }
    setConfirmLoading(true);
    try {
      await api.post("/auth/reset-password/confirm", { token: resetToken, password: newPw });
      setConfirmDone(true);
      toast.success("Password aggiornata. Ora puoi accedere.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Link non valido o scaduto.");
    } finally {
      setConfirmLoading(false);
    }
  };

  const goToLogin = () => { window.location.href = window.location.pathname; };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch {
      setError("Email o password non validi.");
    } finally {
      setLoading(false);
    }
  };

  const doReset = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    try {
      await api.post("/auth/reset-password", { email: email || undefined });
      toast.success("Se l'email è registrata, riceverai un link per reimpostare la password.");
      setShowReset(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossibile inviare il link di reimpostazione.");
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:block lg:w-1/2 relative">
        <img src={BG} alt="Magazzino StorMi" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(19,71,68,0.92), rgba(26,95,90,0.78))" }} />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <StorMiLogo dark />
          <div>
            <h1 className="font-jakarta font-bold text-6xl tracking-tight leading-none" data-testid="login-title">
              Preventivatore
            </h1>
          </div>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 bg-white">
        <div className="w-full max-w-sm slide-up">
          <div className="lg:hidden mb-8 inline-block bg-[#1a5f5a] rounded-lg px-3 py-2"><StorMiLogo /></div>

          {resetToken ? (
            confirmDone ? (
              <div data-testid="reset-confirm-done">
                <h2 className="text-2xl font-display font-semibold mb-1">Password aggiornata</h2>
                <p className="text-slate-500 text-sm mb-8">La tua password è stata reimpostata. Ora puoi accedere con le nuove credenziali.</p>
                <Button onClick={goToLogin} data-testid="reset-goto-login-button"
                  className="w-full bg-[#1a5f5a] hover:bg-[#134744] text-white h-11 rounded-md">
                  Vai al login
                </Button>
              </div>
            ) : (
              <form onSubmit={doConfirm} data-testid="reset-confirm-form">
                <h2 className="text-2xl font-display font-semibold mb-1">Imposta nuova password</h2>
                <p className="text-slate-500 text-sm mb-8">Scegli una nuova password (almeno 8 caratteri).</p>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="new-pw">Nuova password</Label>
                    <Input id="new-pw" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)}
                      placeholder="••••••••" required className="mt-1.5" data-testid="reset-new-password-input" />
                  </div>
                  <div>
                    <Label htmlFor="new-pw2">Conferma password</Label>
                    <Input id="new-pw2" type="password" value={newPw2} onChange={(e) => setNewPw2(e.target.value)}
                      placeholder="••••••••" required className="mt-1.5" data-testid="reset-confirm-password-input" />
                  </div>
                </div>
                <Button type="submit" disabled={confirmLoading} data-testid="reset-confirm-submit-button"
                  className="w-full mt-6 bg-[#1a5f5a] hover:bg-[#134744] text-white h-11 rounded-md">
                  {confirmLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aggiorna password"}
                </Button>
              </form>
            )
          ) : !showReset ? (
            <form onSubmit={submit} data-testid="login-form">
              <h2 className="text-2xl font-display font-semibold mb-1">Accedi</h2>
              <p className="text-slate-500 text-sm mb-8">Inserisci le tue credenziali per continuare.</p>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="nome@azienda.com" required className="mt-1.5" data-testid="login-email-input" />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••" required className="mt-1.5" data-testid="login-password-input" />
                </div>
              </div>

              {error && <p className="text-sm text-red-500 mt-3" data-testid="login-error">{error}</p>}

              <Button type="submit" disabled={loading} data-testid="login-submit-button"
                className="w-full mt-6 bg-[#1a5f5a] hover:bg-[#134744] text-white h-11 rounded-md">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Accedi"}
              </Button>

              <button type="button" onClick={() => setShowReset(true)} data-testid="forgot-password-link"
                className="text-sm text-[#1a5f5a] hover:underline mt-4 block mx-auto">
                Password dimenticata?
              </button>
            </form>
          ) : (
            <form onSubmit={doReset} data-testid="reset-form">
              <button type="button" onClick={() => setShowReset(false)}
                className="flex items-center gap-1 text-sm text-slate-500 mb-4 hover:text-slate-800">
                <ArrowLeft className="h-4 w-4" /> Indietro
              </button>
              <h2 className="text-2xl font-display font-semibold mb-1">Reimposta password</h2>
              <p className="text-slate-500 text-sm mb-8">Ti invieremo un link per reimpostare la password.</p>
              <div>
                <Label htmlFor="reset-email">Email</Label>
                <Input id="reset-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="nome@azienda.com" className="mt-1.5" data-testid="reset-email-input" />
              </div>
              <Button type="submit" disabled={resetLoading} data-testid="reset-submit-button"
                className="w-full mt-6 bg-[#1a5f5a] hover:bg-[#134744] text-white h-11 rounded-md">
                {resetLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Invia link di reimpostazione"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
